import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import StarRating from "../components/StarRating";
import api from "../lib/api";
import { MagnifyingGlass, ShieldCheck, MapPin, Rocket, Sparkle } from "@phosphor-icons/react";
import { isLaunched } from "../lib/launch";
import { useAuth } from "../contexts/AuthContext";

export default function FindTrades() {
  const { user } = useAuth();
  const launched = isLaunched() || (user && user.role === "admin");
  const [sp, setSp] = useSearchParams();
  const trade = sp.get("trade") || "";
  const q = sp.get("q") || "";
  const [trades, setTrades] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState(q);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/meta/trades").then(({ data }) => setTrades(data || []));
  }, []);

  useEffect(() => {
    if (!launched) { setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams();
    if (trade) params.set("trade", trade);
    if (q) params.set("q", q);
    api
      .get(`/tradespeople?${params.toString()}`)
      .then(({ data }) => setItems(data || []))
      .finally(() => setLoading(false));
  }, [trade, q, launched]);

  const applySearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(sp);
    if (search) next.set("q", search); else next.delete("q");
    setSp(next);
  };

  // Pre-launch state: friendly "opening soon" with waitlist CTA
  if (!launched) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-4"
               style={{ color: "var(--brand-orange)" }}>
            <Sparkle size={14} weight="fill" /> Launching soon
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              style={{ color: "var(--brand-green)" }}
              data-testid="find-trades-prelaunch-heading">
            Browsing opens on launch day
          </h1>
          <p className="mt-5 text-[16px] sm:text-lg text-[var(--text-muted)] leading-relaxed max-w-2xl mx-auto">
            We're onboarding the first wave of verified UK tradespeople right now.
            Join the waiting list and we'll email you the moment your area goes live —
            plus you get first-in-line priority to book.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold shadow-lg shadow-emerald-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5"
              style={{ background: "var(--brand-green)", color: "#fff" }}
              data-testid="find-trades-join-waitlist"
            >
              <Rocket size={18} weight="fill" />
              Join the waiting list
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold border-2 hover:bg-white transition-colors"
              style={{ borderColor: "var(--brand-green)", color: "var(--brand-green)" }}
              data-testid="find-trades-how"
            >
              How MyHandyHub works
            </Link>
          </div>

          <div className="mt-10 text-xs text-[var(--text-muted)] flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} weight="fill" style={{ color: "var(--brand-green)" }} /> ID-verified trades</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} weight="fill" style={{ color: "var(--brand-green)" }} /> Local postcode matching</span>
            <span className="flex items-center gap-1.5"><MagnifyingGlass size={14} weight="bold" style={{ color: "var(--brand-green)" }} /> £3.99/month · no lead fees</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>Find tradespeople</h1>
        <p className="text-[var(--text-muted)] mt-2">Browse verified UK trades by category.</p>

        <form onSubmit={applySearch} className="mt-6 flex gap-3" data-testid="trades-search-form">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              data-testid="trades-search-input"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, business or postcode…"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
            />
          </div>
          <button className="btn-primary" data-testid="trades-search-submit">Search</button>
        </form>

        <div className="mt-5 flex gap-2 overflow-x-auto scrollbar-thin pb-2" data-testid="trades-pill-row">
          <button
            onClick={() => { const n = new URLSearchParams(sp); n.delete("trade"); setSp(n); }}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border ${!trade ? "bg-[var(--brand-green)] text-white border-[var(--brand-green)]" : "bg-white text-[var(--text-muted)] border-[var(--border-stone)]"}`}
            data-testid="trade-pill-all"
          >All trades</button>
          {trades.map((t) => (
            <button
              key={t.slug}
              onClick={() => { const n = new URLSearchParams(sp); n.set("trade", t.slug); setSp(n); }}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border ${trade===t.slug ? "bg-[var(--brand-green)] text-white border-[var(--brand-green)]" : "bg-white text-[var(--text-muted)] border-[var(--border-stone)]"}`}
              data-testid={`trade-pill-${t.slug}`}
            >{t.name}</button>
          ))}
        </div>

        {loading ? (
          <p className="text-[var(--text-muted)] mt-10">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-10 text-center py-16 brand-card">
            <p className="text-[var(--text-muted)]">No tradespeople yet — be the first to join!</p>
            <Link to="/register?role=tradesperson" className="btn-primary mt-4 inline-flex" data-testid="empty-join-trader">Join as a tradesperson</Link>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="trades-results">
            {items.map((u) => (
              <Link
                to={`/trades/${u.user_id}`}
                key={u.user_id}
                className="brand-card p-5"
                data-testid={`trader-card-${u.user_id}`}
              >
                <div className="flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full object-cover border border-[var(--border-stone)]" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)] font-display font-bold flex items-center justify-center">{(u.name || "?")[0]}</div>
                  )}
                  <div className="flex-1">
                    <div className="font-display font-semibold text-[var(--brand-green)]">{u.business_name || u.name}</div>
                    <StarRating value={u.rating_avg || 0} count={u.rating_count || 0} />
                  </div>
                  {u.verified && (
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)]">
                      <ShieldCheck size={11} weight="fill" className="inline -mt-0.5 mr-0.5" />Verified
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm text-[var(--text-muted)] line-clamp-2">{u.bio || "Professional tradesperson on MyHandyHub."}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(u.trades || []).slice(0, 3).map((t) => (
                    <span key={t} className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[rgba(226,109,41,0.1)] text-[var(--brand-orange)]">{t}</span>
                  ))}
                </div>
                {u.postcode_area && (
                  <p className="mt-3 text-xs text-[var(--text-muted)] flex items-center gap-1">
                    <MapPin size={12} /> {u.postcode_area} · up to {u.work_range_miles} mi
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
